import { useMemo, useState } from "react";
import { Checkbox, Flex, Form, Tooltip, Typography } from "antd";
import { MdOutlineArrowOutward } from "react-icons/md";

import FormInput from "./FormInput";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import { deleteDefaultAddressMsg, formRules } from "../../constant";
import PrimaryButton from "./PrimaryButton";
import SelectTag from "./SelectTag";
import { getAddressModApiBody, usePendingModification } from "../../pages/Profile";
import { useGlobalModal } from "../../context/SuccessModalProvider";

const { Title } = Typography;

const Address = ({ title, formData, handleSubmit, runDefaultHandleSubmit = true, closeModal }) => {
  const { pending_modification } = usePendingModification() || {};
  const { postApi, loading } = useApiCall();
  const user_id = useSelector((state) => state.auth.user_id);
  const full_name = useSelector((state) => state.auth.full_name);
  const { is_sales_manager, is_admin, is_customer } = useSelector((state) => state.auth);
  const { showPopup } = useGlobalModal();

  // A memoized key that changes when formData's JSON string changes.
  // This forces a re-render of the entire form.
  const formKey = useMemo(() => {
    return JSON.stringify(formData);
  }, [formData]);

  const onFinish = async (values) => {
    if (runDefaultHandleSubmit) {
      const { apiRoute, body } = getAddressModApiBody({
        full_name,
        is_sales_manager: is_sales_manager || is_admin,
        formData,
        pending_modification,
        existingBody: {
          updated_by: full_name,
          updated_by_id: user_id,
          is_default_billing: values.is_default_billing ? 1 : 0,
          is_default_delivery: values.is_default_delivery ? 1 : 0,
        },
        is_customer: is_customer,
        customer_id: formData.customer_id,
      });

      try {
        const response = await postApi(apiRoute, { ...formData, ...values, ...body }, true);
        if (response?.status) {
          closeModal();
          console.log("Address saved successfully");
        }
      } catch (error) {
        console.log("Error", error);
      }
    }
    if (handleSubmit) {
      handleSubmit(values);
    }
  };

  return (
    <>
      <Title className="input-css font-abhaya" level={2}>
        {title || (formData.type === "add" ? "Add address" : "Edit address")}
      </Title>
      <Form
        // The most reliable solution: use a key to force a re-mount.
        key={formKey}
        className="mt-6"
        name="address-form"
        layout="vertical"
        autoComplete="off"
        onFinish={onFinish}
        initialValues={formData}
      >
        <FormInput label="Address Title" name="title" rules={formRules.required} />
        <FormInput label="Address Line 1" name="address_line_1" rules={formRules.required} />
        <FormInput label="Address Line 2" name="address_line_2" rules={formRules.required} />
        <Flex justify="space-between">
          <FormInput label="City" name="city" rules={formRules.required} style={{ width: "48%" }} />
          <Form.Item rules={formRules.required} label="State" name="state" style={{ width: "48%" }}>
            <SelectTag />
          </Form.Item>
        </Flex>
        <FormInput
          label="Postal Code"
          name="postal_code"
          rules={formRules.postalCode}
          maxLength={6}
        />
        <div className="flex items-center gap-5">
          <Typography.Text>Make As Default</Typography.Text>
          <Form.Item name="is_default_billing" className="mb-0" valuePropName="checked">
            <Checkbox className="custom-checkbox" disabled={formData.is_default_billing}>
              <Tooltip
                title={formData.is_default_billing ? deleteDefaultAddressMsg("billing") : undefined}
                styles={{
                  body: {
                    width: "fit-content",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                Billing
              </Tooltip>
            </Checkbox>
          </Form.Item>
          <Form.Item name="is_default_delivery" className="mb-0" valuePropName="checked">
            <Checkbox className="custom-checkbox" disabled={formData.is_default_delivery}>
              <Tooltip
                title={
                  formData.is_default_delivery ? deleteDefaultAddressMsg("delivery") : undefined
                }
                styles={{
                  body: {
                    width: "fit-content",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                Delivery
              </Tooltip>
            </Checkbox>
          </Form.Item>
        </div>
        <div className="w-full mt-4 flex justify-center">
          <PrimaryButton
            type="submit"
            text="Save and Proceed"
            className="!mr-[35px]"
            loading={loading}
          />
        </div>
      </Form>
    </>
  );
};

export default Address;
